import React, { useState, useEffect, useRef } from 'react';
import {
    Bell, Send, Users, User, CheckCircle, XCircle, AlertCircle,
    Search, X, Loader
} from 'lucide-react';
import api from '../services/api';
import { adminNotificationAPI } from '../services/api';

const TABS = [
    { key: 'single', label: 'Single User', icon: User },
    { key: 'all', label: 'Everyone', icon: Users },
    { key: 'subscribers', label: 'Subscribers', icon: Bell }
];

const Toggle = ({ value, onToggle }) => (
    <button
        onClick={onToggle}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none ${
            value ? 'bg-blue-600' : 'bg-gray-600'
        }`}
    >
        <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200 ${
                value ? 'translate-x-6' : 'translate-x-1'
            }`}
        />
    </button>
);

const NotificationsManagement = () => {
    const [tab, setTab] = useState('single');

    // Compose fields
    const [title, setTitle] = useState('');
    const [message, setMessage] = useState('');
    const [link, setLink] = useState('');
    const [sending, setSending] = useState(false);
    const [result, setResult] = useState(null);
    const [confirmSend, setConfirmSend] = useState(false);

    // Single-user picker
    const [allUsers, setAllUsers] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [singleUser, setSingleUser] = useState(null);
    const [userSearch, setUserSearch] = useState('');
    const [userResults, setUserResults] = useState([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const searchRef = useRef(null);

    // Bulk recipient counts
    const [recipientCount, setRecipientCount] = useState(0);
    const [loadingCount, setLoadingCount] = useState(false);

    // Automatic types
    const [types, setTypes] = useState([]);
    const [loadingTypes, setLoadingTypes] = useState(true);

    useEffect(() => {
        const load = async () => {
            setLoadingTypes(true);
            try {
                const res = await adminNotificationAPI.getTypes();
                if (res.success) setTypes(res.data.filter(t => t.is_automatic));
            } catch (err) {
                console.error('Error loading notification types:', err);
            } finally {
                setLoadingTypes(false);
            }
        };
        load();
    }, []);

    useEffect(() => {
        if (tab !== 'single' || allUsers.length > 0) return;
        const load = async () => {
            setLoadingUsers(true);
            try {
                const res = await api.get('/admin/email/recipients?filter=all');
                if (res.data.success) setAllUsers(res.data.data);
            } catch {
                /* ignore */
            } finally {
                setLoadingUsers(false);
            }
        };
        load();
    }, [tab, allUsers.length]);

    useEffect(() => {
        if (tab !== 'single') return;
        const q = userSearch.toLowerCase().trim();
        if (!q) { setUserResults(allUsers.slice(0, 8)); return; }
        const matches = allUsers.filter((u) =>
            (u.display_name || '').toLowerCase().includes(q) ||
            (u.username || '').toLowerCase().includes(q) ||
            (u.email || '').toLowerCase().includes(q)
        ).slice(0, 8);
        setUserResults(matches);
        setShowDropdown(true);
    }, [userSearch, allUsers, tab]);

    useEffect(() => {
        const handler = (e) => {
            if (searchRef.current && !searchRef.current.contains(e.target)) setShowDropdown(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    useEffect(() => {
        if (tab !== 'all' && tab !== 'subscribers') return;
        const load = async () => {
            setLoadingCount(true);
            try {
                const res = await adminNotificationAPI.getRecipientCount(tab);
                if (res.success) setRecipientCount(res.data.count);
            } catch {
                setRecipientCount(0);
            } finally {
                setLoadingCount(false);
            }
        };
        load();
    }, [tab]);

    const selectUser = (u) => {
        setSingleUser(u);
        setUserSearch('');
        setUserResults([]);
        setShowDropdown(false);
        setResult(null);
    };
    const clearUser = () => { setSingleUser(null); setUserSearch(''); setUserResults([]); };

    const handleTabChange = (key) => {
        setTab(key);
        setResult(null);
        setConfirmSend(false);
    };

    const canSend = title.trim() && message.trim() && (tab !== 'single' || singleUser);

    const doSend = async () => {
        setSending(true);
        setResult(null);
        setConfirmSend(false);
        try {
            const payload = {
                target: tab,
                title: title.trim(),
                message: message.trim(),
                link: link.trim() || null,
                ...(tab === 'single' ? { userId: singleUser.id } : {})
            };
            const res = await adminNotificationAPI.send(payload);
            setResult({ success: res.success, message: res.message });
            if (res.success) {
                setTitle(''); setMessage(''); setLink(''); setSingleUser(null);
            }
        } catch (err) {
            setResult({ success: false, message: err.response?.data?.message || err.message });
        } finally {
            setSending(false);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!canSend) return;
        if (tab === 'single') {
            doSend();
        } else {
            setConfirmSend(true);
        }
    };

    const toggleType = async (typeKey, isActive) => {
        setTypes(prev => prev.map(t => (t.type_key === typeKey ? { ...t, is_active: isActive } : t)));
        try {
            await adminNotificationAPI.updateType(typeKey, isActive);
        } catch (err) {
            console.error('Error updating notification type:', err);
        }
    };

    const inputCls = 'w-full bg-gray-800 border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-blue-500';
    const labelCls = 'block text-xs text-gray-400 mb-1.5 font-medium uppercase tracking-wide';

    return (
        <div className="min-h-screen bg-gray-900 pt-20 pb-12">
            <div className="max-w-4xl mx-auto px-6">
                <div className="mb-8">
                    <h1 className="text-3xl font-black gradient-text mb-1">Notifications Management</h1>
                    <p className="text-gray-400 text-sm">Broadcast messages and control automatic notification types</p>
                </div>

                {/* Tabs */}
                <div className="flex flex-wrap gap-2 mb-6">
                    {TABS.map(({ key, label, icon: Icon }) => (
                        <button key={key} onClick={() => handleTabChange(key)}
                            className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${
                                tab === key ? 'bg-blue-600 text-white' : 'glass text-gray-400 hover:text-white'
                            }`}>
                            <Icon className="w-4 h-4" />
                            {label}
                        </button>
                    ))}
                </div>

                {/* Compose panel */}
                <div className="glass-card p-6 mb-8">
                    <h2 className="text-lg font-bold text-white mb-5 flex items-center gap-2">
                        <Send className="w-5 h-5 text-blue-400" />
                        Compose
                    </h2>

                    <form onSubmit={handleSubmit} className="space-y-4">
                        {tab === 'single' && (
                            <div ref={searchRef}>
                                <label className={labelCls}>To</label>
                                {singleUser ? (
                                    <div className="flex items-center gap-2 px-3 py-2 bg-blue-600/20 border border-blue-500/30 rounded-lg">
                                        <div className="w-7 h-7 rounded-full bg-blue-600/40 flex items-center justify-center flex-shrink-0">
                                            <User className="w-3.5 h-3.5 text-blue-300" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm text-white font-medium truncate">{singleUser.display_name || singleUser.username}</p>
                                            <p className="text-xs text-blue-300 truncate">@{singleUser.username}</p>
                                        </div>
                                        <button type="button" onClick={clearUser} aria-label="Clear selected user" className="text-gray-400 hover:text-white flex-shrink-0">
                                            <X className="w-4 h-4" />
                                        </button>
                                    </div>
                                ) : (
                                    <div className="relative">
                                        <Search className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                                        <input type="text" value={userSearch}
                                            onChange={(e) => setUserSearch(e.target.value)}
                                            onFocus={() => setShowDropdown(allUsers.length > 0)}
                                            placeholder={loadingUsers ? 'Loading users…' : 'Search by name, username, or email'}
                                            className={`${inputCls} pl-9`} autoComplete="off" />
                                        {showDropdown && userResults.length > 0 && (
                                            <div className="absolute z-20 top-full mt-1 left-0 right-0 bg-gray-800 border border-white/10 rounded-lg shadow-xl overflow-hidden">
                                                {userResults.map((u) => (
                                                    <button key={u.id} type="button" onClick={() => selectUser(u)}
                                                        className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-700 transition-colors text-left">
                                                        <div className="w-7 h-7 rounded-full bg-gray-700 flex items-center justify-center flex-shrink-0">
                                                            <User className="w-3.5 h-3.5 text-gray-400" />
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-sm text-white font-medium truncate">
                                                                {u.display_name || u.username}
                                                                {u.username && u.display_name && <span className="text-gray-500 font-normal ml-1">@{u.username}</span>}
                                                            </p>
                                                            <p className="text-xs text-gray-400 truncate">{u.email}</p>
                                                        </div>
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}

                        {(tab === 'all' || tab === 'subscribers') && (
                            <div className="p-3 bg-gray-800 rounded-lg border border-white/10 text-sm text-gray-400">
                                {loadingCount ? 'Loading recipient count…' : (
                                    <>Will send to <strong className="text-white">{recipientCount}</strong> {tab === 'subscribers' ? 'active subscriber' : 'user'}{recipientCount !== 1 ? 's' : ''}.</>
                                )}
                            </div>
                        )}

                        <div>
                            <label className={labelCls}>Title</label>
                            <input type="text" value={title} onChange={(e) => setTitle(e.target.value)}
                                placeholder="Notification title" className={inputCls} required />
                        </div>

                        <div>
                            <label className={labelCls}>Message</label>
                            <textarea value={message} onChange={(e) => setMessage(e.target.value)}
                                rows={4} placeholder="Your message…" className={inputCls} required />
                        </div>

                        <div>
                            <label className={labelCls}>Link (optional)</label>
                            <input type="text" value={link} onChange={(e) => setLink(e.target.value)}
                                placeholder="/feed" className={inputCls} />
                        </div>

                        {result && (
                            <div className={`flex items-start gap-2 p-3 rounded-lg text-sm ${
                                result.success
                                    ? 'bg-green-500/10 text-green-400 border border-green-500/20'
                                    : 'bg-red-500/10 text-red-400 border border-red-500/20'
                            }`}>
                                {result.success ? <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" /> : <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />}
                                {result.message}
                            </div>
                        )}

                        {tab === 'single' ? (
                            <button type="submit" disabled={sending || !canSend}
                                className="w-full flex items-center justify-center gap-2 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors">
                                {sending ? <><Loader className="w-4 h-4 animate-spin" /> Sending…</> : <><Send className="w-4 h-4" /> Send Notification</>}
                            </button>
                        ) : !confirmSend ? (
                            <button type="submit" disabled={sending || !canSend}
                                className="w-full flex items-center justify-center gap-2 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors">
                                <Send className="w-4 h-4" />
                                Send to {recipientCount} Recipient{recipientCount !== 1 ? 's' : ''}
                            </button>
                        ) : (
                            <div className="space-y-2">
                                <div className="flex items-start gap-2 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20 text-yellow-400 text-sm">
                                    <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                                    <span>This will notify <strong>{recipientCount}</strong> user{recipientCount !== 1 ? 's' : ''}. This cannot be undone.</span>
                                </div>
                                <div className="flex gap-2">
                                    <button type="button" onClick={() => setConfirmSend(false)}
                                        className="flex-1 py-2.5 glass text-gray-300 hover:text-white rounded-lg text-sm font-medium transition-colors">
                                        Cancel
                                    </button>
                                    <button type="button" onClick={doSend} disabled={sending}
                                        className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white font-semibold rounded-lg transition-colors">
                                        {sending ? <><Loader className="w-4 h-4 animate-spin" /> Sending…</> : 'Confirm Send'}
                                    </button>
                                </div>
                            </div>
                        )}
                    </form>
                </div>

                {/* Automatic types */}
                <div className="glass-card p-6">
                    <h2 className="text-lg font-bold text-white mb-1 flex items-center gap-2">
                        <Bell className="w-5 h-5 text-green-400" />
                        Automatic Notifications
                    </h2>
                    <p className="text-sm text-gray-400 mb-5">Turn off any automatic notification type platform-wide.</p>

                    {loadingTypes ? (
                        <p className="text-sm text-gray-500">Loading…</p>
                    ) : (
                        <div className="space-y-4">
                            {types.map((t) => (
                                <div key={t.type_key} className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm text-white">{t.label}</p>
                                        <p className="text-xs text-gray-400">{t.description}</p>
                                    </div>
                                    <Toggle value={!!t.is_active} onToggle={() => toggleType(t.type_key, !t.is_active)} />
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default NotificationsManagement;
